import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import posthog from 'posthog-js'
import ErrorBoundary from '@/components/layout/ErrorBoundary'
import ProtectedRoute from '@/components/layout/ProtectedRoute'
import PublicLayout from '@/components/layout/PublicLayout'
// Landing is eager so React renders it directly on mount, making FCP and LCP
// fire simultaneously instead of showing a Suspense "Loading…" placeholder
// first (which was creating a 2.5s FCP→LCP gap). At 5 KB gzip it adds
// negligible weight to the main bundle.
import Landing from '@/pages/Landing'

// All other pages are lazy so Vite splits them into per-page chunks.
// Layout wrappers (ProtectedRoute, PublicLayout, ErrorBoundary) stay eager
// because they are needed on every render to apply the gate logic.
const SignIn        = lazy(() => import('@/pages/SignIn'))
const Enroll        = lazy(() => import('@/pages/Enroll'))
const Demo          = lazy(() => import('@/pages/Demo'))
const Privacy       = lazy(() => import('@/pages/Privacy'))
const About         = lazy(() => import('@/pages/About'))
const Setup         = lazy(() => import('@/pages/Setup'))
const Join          = lazy(() => import('@/pages/Join'))
const Welcome       = lazy(() => import('@/pages/Welcome'))
const CreateVillage = lazy(() => import('@/pages/onboarding/CreateVillage'))
const Dashboard     = lazy(() => import('@/pages/Dashboard'))
const Needs         = lazy(() => import('@/pages/Needs'))
const Villagers     = lazy(() => import('@/pages/Villagers'))
const VillagerProfile = lazy(() => import('@/pages/VillagerProfile'))
const CareProProfile  = lazy(() => import('@/pages/CareProProfile'))
const Insights      = lazy(() => import('@/pages/Insights'))
const Profile       = lazy(() => import('@/pages/Profile'))
const NotFound      = lazy(() => import('@/pages/NotFound'))

function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href })
  }, [location.pathname])
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <PageViewTracker />
      <Suspense fallback={
        <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#fbf9f4' }}>
          <span style={{ color: '#4a7d5d', fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem' }}>Loading…</span>
        </div>
      }>
      <Routes>
        {/* Landing has its own Footer — excluded from PublicLayout */}
        <Route path="/" element={<Landing />} />

        {/* Public pages with global footer */}
        <Route element={<PublicLayout />}>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/enroll" element={<Enroll />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
        </Route>

        {/* Authenticated pages — TopNav + Footer via ProtectedRoute */}
        <Route element={<ProtectedRoute />}>
          <Route path="/setup" element={<Setup />} />
          <Route path="/join" element={<Join />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/onboarding/create-village" element={<CreateVillage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/needs" element={<Needs />} />
          <Route path="/villagers" element={<Villagers />} />
          <Route path="/villagers/:id" element={<VillagerProfile />} />
          <Route path="/villagers/care-pros/:id" element={<CareProProfile />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/profile" element={<Profile />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
